import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Nav, Card, Image, Button, Table, ProgressBar, Pagination, Modal } from '@themesberg/react-bootstrap';
import { Link } from 'react-router-dom';

import { Routes } from "../routes";
import { pageVisits, pageTraffic, pageRanking } from "../data/tables";
import transactions from "../data/transactions";

const ValueChange = ({ value, suffix }) => {
  const valueIcon = value < 0 ? faAngleDown : faAngleUp;
  const valueTxtColor = value < 0 ? "text-danger" : "text-success";

  return (
    value ? <span className={valueTxtColor}>
      <FontAwesomeIcon icon={valueIcon} />
      <span className="fw-bold ms-1">
        {Math.abs(value)}{suffix}
      </span>
    </span> : "--"
  );
};

export const PageVisitsTable = () => {
  const TableRow = (props) => {
    const { pageName, views, returnValue, bounceRate } = props;
    const bounceIcon = bounceRate < 0 ? faArrowDown : faArrowUp;
    const bounceTxtColor = bounceRate < 0 ? "text-danger" : "text-success";

    return (
      <tr>
        <th scope="row">{pageName}</th>
        <td>{views}</td>
        <td>${returnValue}</td>
        <td>
          <FontAwesomeIcon icon={bounceIcon} className={`${bounceTxtColor} me-3`} />
          {Math.abs(bounceRate)}%
        </td>
      </tr>
    );
  };

  return (
    <Card border="light" className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5>Page visits</h5>
          </Col>
          <Col className="text-end">
            <Button variant="secondary" size="sm">See all</Button>
          </Col>
        </Row>
      </Card.Header>
      <Table responsive className="align-items-center table-flush">
        <thead className="thead-light">
          <tr>
            <th scope="col">Page name</th>
            <th scope="col">Page Views</th>
            <th scope="col">Page Value</th>
            <th scope="col">Bounce rate</th>
          </tr>
        </thead>
        <tbody>
          {pageVisits.map(pv => <TableRow key={`page-visit-${pv.id}`} {...pv} />)}
        </tbody>
      </Table>
    </Card>
  );
};

export const PageTrafficTable = () => {
  const TableRow = (props) => {
    const { id, source, sourceIcon, sourceIconColor, sourceType, category, rank, trafficShare, change } = props;

    return (
      <tr>
        <td>
          <Card.Link href="#" className="text-primary fw-bold">{id}</Card.Link>
        </td>
        <td className="fw-bold">
          <FontAwesomeIcon icon={sourceIcon} className={`icon icon-xs text-${sourceIconColor} w-30`} />
          {source}
        </td>
        <td>{sourceType}</td>
        <td>{category ? category : "--"}</td>
        <td>{rank ? rank : "--"}</td>
        <td>
          <Row className="d-flex align-items-center">
            <Col xs={12} xl={2} className="px-0">
              <small className="fw-bold">{trafficShare}%</small>
            </Col>
            <Col xs={12} xl={10} className="px-0 px-xl-1">
              <ProgressBar variant="primary" className="progress-lg mb-0" now={trafficShare} min={0} max={100} />
            </Col>
          </Row>
        </td>
        <td>
          <ValueChange value={change} suffix="%" />
        </td>
      </tr>
    );
  };

  return (
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body className="pb-0">
        <Table responsive className="table-centered table-nowrap rounded mb-0">
          <thead className="thead-light">
            <tr>
              <th className="border-0">#</th>
              <th className="border-0">Traffic Source</th>
              <th className="border-0">Source Type</th>
              <th className="border-0">Category</th>
              <th className="border-0">Global Rank</th>
              <th className="border-0">Traffic Share</th>
              <th className="border-0">Change</th>
            </tr>
          </thead>
          <tbody>
            {pageTraffic.map(pt => <TableRow key={`page-traffic-${pt.id}`} {...pt} />)}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export const RankingTable = () => {
  const TableRow = (props) => {
    const { country, countryImage, overallRank, overallRankChange, travelRank, travelRankChange, widgetsRank, widgetsRankChange } = props;

    return (
      <tr>
        <td className="border-0">
          <Card.Link href="#" className="d-flex align-items-center">
            <Image src={countryImage} className="image-small rounded-circle me-2" />
            <div><span className="h6">{country}</span></div>
          </Card.Link>
        </td>
        <td className="fw-bold border-0">
          {overallRank ? overallRank : "-"}
        </td>
        <td className="border-0">
          <ValueChange value={overallRankChange} />
        </td>
        <td className="fw-bold border-0">
          {travelRank ? travelRank : "-"}
        </td>
        <td className="border-0">
          <ValueChange value={travelRankChange} />
        </td>
        <td className="fw-bold border-0">
          {widgetsRank ? widgetsRank : "-"}
        </td>
        <td className="border-0">
          <ValueChange value={widgetsRankChange} />
        </td>
      </tr>
    );
  };

  return (
    <Card border="light" className="shadow-sm">
      <Card.Body className="pb-0">
        <Table responsive className="table-centered table-nowrap rounded mb-0">
          <thead className="thead-light">
            <tr>
              <th className="border-0">Country</th>
              <th className="border-0">All</th>
              <th className="border-0">All Change</th>
              <th className="border-0">Travel & Local</th>
              <th className="border-0">Travel & Local Change</th>
              <th className="border-0">Widgets</th>
              <th className="border-0">Widgets Change</th>
            </tr>
          </thead>
          <tbody>
            {pageRanking.map(r => <TableRow key={`ranking-${r.id}`} {...r} />)}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export const TransactionsTable = () => {

  const [currentPage, setCurrentPage] = React.useState(1);
  const [rows, setRows] = React.useState(transactions);
  const [showModal, setShowModal] = React.useState(false);
  const [editData, setEditData] = React.useState(null);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [newData, setNewData] = React.useState({
    invoiceNumber: '',
    subscription: '',
    issueDate: '',
    dueDate: '',
    price: '',
    status: 'Paid'
  });
  const itemsPerPage = window.innerWidth < 768 ? 5 : 10;
  const totalPages = Math.ceil(rows.length / itemsPerPage);

  const paginatedRows = rows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDelete = (invoiceNumber) => {
    setRows(rows.filter(row => row.invoiceNumber !== invoiceNumber));
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowModal(true);
  };

  const handleSave = () => {
    setRows(rows.map(row => row.invoiceNumber === editData.invoiceNumber ? editData : row));
    setShowModal(false);
  };

  const handleViewDetails = (row) => {
    alert(`Details for ${row.invoiceNumber}:\nBill For: ${row.subscription}\nIssue Date: ${row.issueDate}\nDue Date: ${row.dueDate}\nTotal: $${row.price}\nStatus: ${row.status}`);
  };

  const TableRow = (props) => {
    const { invoiceNumber, subscription, price, issueDate, dueDate, status } = props;
    const statusVariant = status === "Paid" ? "success"
      : status === "Due" ? "warning"
        : status === "Canceled" ? "danger" : "primary";
    return (
      <tr>
        <td>
          <Card.Link as={Link} to={Routes.Invoice.path} className="fw-normal">
            {invoiceNumber}
          </Card.Link>
        </td>
        <td>
          <span className="fw-normal">
            {subscription}
          </span>
        </td>
        <td>
          <span className="fw-normal">
            {issueDate}
          </span>
        </td>
        <td>
          <span className="fw-normal">
            {dueDate}
          </span>
        </td>
        <td>
          <span className="fw-normal">
            ${parseFloat(price).toFixed(2)}
          </span>
        </td>
        <td>
          <span className={`fw-normal text-${statusVariant}`}>
            {status}
          </span>
        </td>
        <td>
          <Button variant="info" size="sm" className="me-2" onClick={() => handleViewDetails(props)}>
            Details
          </Button>
          <Button variant="warning" size="sm" className="me-2" onClick={() => handleEdit(props)}>
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDelete(invoiceNumber)}>
            Delete
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Card border="light" className="table-wrapper table-responsive shadow-sm">
      <Card.Header style={{ backgroundColor: '#001f3f', color: 'white' }}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-white">Transactions Table</h5>
          <Button variant="light" onClick={() => setShowAddModal(true)}>
            Add New
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="pt-0">
        <Table hover className="user-table align-items-center">
          <thead>
            <tr>
              <th className="border-bottom">#</th>
              <th className="border-bottom">Bill For</th>
              <th className="border-bottom">Issue Date</th>
              <th className="border-bottom">Due Date</th>
              <th className="border-bottom">Total</th>
              <th className="border-bottom">Status</th>
              <th className="border-bottom">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map(t => <TableRow key={`transaction-${t.invoiceNumber}`} {...t} />)}
          </tbody>
        </Table>
        <Card.Footer className="px-3 border-0 d-lg-flex align-items-center justify-content-between">
          <Nav>
            <Pagination className="mb-2 mb-lg-0">
              <Pagination.Prev
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Pagination.Prev>
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          </Nav>
          <small className="fw-bold">
            Showing <b>{paginatedRows.length}</b> out of <b>{rows.length}</b> entries
          </small>
        </Card.Footer>
        {/* Edit Modal */}
        <Card.Footer>
          <Modal show={showModal} onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Edit Transaction</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editData && (
                <form>
                  <div className="mb-2">
                    <label>Bill For</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editData.subscription}
                      onChange={e => setEditData({ ...editData, subscription: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Issue Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editData.issueDate}
                      onChange={e => setEditData({ ...editData, issueDate: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Due Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editData.dueDate}
                      onChange={e => setEditData({ ...editData, dueDate: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Total</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editData.price}
                      onChange={e => setEditData({ ...editData, price: e.target.value })}
                    />
                  </div>
                  <div className="mb-2">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={editData.status}
                      onChange={e => setEditData({ ...editData, status: e.target.value })}
                    >
                      <option>Paid</option>
                      <option>Due</option>
                      <option>Canceled</option>
                    </select>
                  </div>
                </form>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Save Changes
              </Button>
            </Modal.Footer>
          </Modal>
          {/* Add Modal */}
          <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Add New Transaction</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form>
                <div className="mb-2">
                  <label>Invoice Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newData.invoiceNumber}
                    onChange={e => setNewData({ ...newData, invoiceNumber: e.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label>Bill For</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newData.subscription}
                    onChange={e => setNewData({ ...newData, subscription: e.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label>Issue Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={newData.issueDate}
                    onChange={e => setNewData({ ...newData, issueDate: e.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label>Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={newData.dueDate}
                    onChange={e => setNewData({ ...newData, dueDate: e.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label>Total</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newData.price}
                    onChange={e => setNewData({ ...newData, price: e.target.value })}
                  />
                </div>
                <div className="mb-2">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={newData.status}
                    onChange={e => setNewData({ ...newData, status: e.target.value })}
                  >
                    <option>Paid</option>
                    <option>Due</option>
                    <option>Canceled</option>
                  </select>
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
                setRows([
                  ...rows,
                  { ...newData }
                ]);
                setShowAddModal(false);
                setNewData({
                  invoiceNumber: '',
                  subscription: '',
                  issueDate: '',
                  dueDate: '',
                  price: '',
                  status: 'Paid'
                });
              }}>
                Add
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Footer>
      </Card.Body>
    </Card>
  );
}